import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState } from 'react';
import { X, Printer, Download, Save, FileText, Layout, FileDown } from 'lucide-react';
import { Button } from '../ui/Button';
import { MarkdownViewer } from './markdown-viewer';
import { LetterSizePreview } from './LetterSizePreview';
export function FullScreenDocumentViewer({ markdownUrl, generatedMarkdown, onClose, onMoveToSetup, documentTitle = 'Generated Document' }) {
    const [selectedFormat, setSelectedFormat] = useState('pdf');
    const [isDownloading, setIsDownloading] = useState(false);
    const [viewMode, setViewMode] = useState('standard');
    const handlePrint = () => {
        window.print();
    };
    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            const blob = new Blob([generatedMarkdown], { type: getMimeType(selectedFormat) });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${documentTitle.toLowerCase().replace(/\s+/g, '-')}.${selectedFormat}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
        catch (error) {
            console.error('Download failed:', error);
            alert('Failed to download document. Please try again.');
        }
        finally {
            setIsDownloading(false);
        }
    };
    const getMimeType = (format) => {
        const mimeTypes = {
            pdf: 'application/pdf',
            docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            html: 'text/html',
            md: 'text/markdown'
        };
        return mimeTypes[format] || 'text/plain';
    };
    return (_jsxs("div", { className: "fixed inset-0 z-50 flex flex-col", style: {
            backgroundColor: 'var(--bg-base)'
        }, children: [_jsxs("div", { className: "flex items-center justify-between px-6 py-4 border-b print:hidden", style: {
                    backgroundColor: 'var(--bg-elevated)',
                    borderColor: 'var(--border-color)'
                }, children: [_jsxs("div", { className: "flex items-center gap-4", children: [_jsx(FileText, { className: "w-6 h-6", style: { color: '#D97706' } }), _jsxs("div", { children: [_jsx("h2", { className: "text-xl font-semibold", style: { color: 'var(--text-primary)' }, children: documentTitle }), _jsx("p", { className: "text-sm", style: { color: 'var(--text-secondary)' }, children: "Full Screen Preview" })] })] }), _jsxs("div", { className: "flex items-center gap-3", children: [_jsxs("div", { className: "flex items-center gap-2 px-2 py-1 rounded-lg", style: { backgroundColor: 'var(--bg-base)' }, children: [_jsxs("button", { onClick: () => setViewMode('standard'), className: `px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${viewMode === 'standard'
                                            ? 'bg-amber-600 text-white'
                                            : 'text-gray-600 hover:text-gray-900'}`, style: viewMode === 'standard' ? { backgroundColor: '#D97706', color: 'white' } : {}, children: [_jsx(Layout, { className: "w-4 h-4" }), "Standard"] }), _jsxs("button", { onClick: () => setViewMode('letter'), className: `px-3 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${viewMode === 'letter'
                                            ? 'bg-amber-600 text-white'
                                            : 'text-gray-600 hover:text-gray-900'}`, style: viewMode === 'letter' ? { backgroundColor: '#D97706', color: 'white' } : {}, children: [_jsx(FileDown, { className: "w-4 h-4" }), "Letter Size"] })] }), viewMode === 'standard' && (_jsxs("div", { className: "flex items-center gap-2", children: [_jsx("span", { className: "text-sm font-medium", style: { color: 'var(--text-secondary)' }, children: "Format:" }), _jsxs("select", { value: selectedFormat, onChange: (e) => setSelectedFormat(e.target.value), className: "px-3 py-2 rounded-lg text-sm", style: {
                                            backgroundColor: 'var(--bg-base)',
                                            border: '1px solid var(--border-color)',
                                            color: 'var(--text-primary)'
                                        }, children: [_jsx("option", { value: "pdf", children: "PDF" }), _jsx("option", { value: "docx", children: "DOCX" }), _jsx("option", { value: "html", children: "HTML" }), _jsx("option", { value: "md", children: "Markdown" })] })] })), viewMode === 'standard' && (_jsxs(_Fragment, { children: [_jsxs(Button, { variant: "outline", onClick: handlePrint, style: { borderColor: '#D97706', color: '#D97706' }, children: [_jsx(Printer, { className: "w-4 h-4 mr-2" }), "Print"] }), _jsxs(Button, { variant: "outline", onClick: handleDownload, disabled: isDownloading, style: { borderColor: '#10b981', color: '#10b981' }, children: [_jsx(Download, { className: "w-4 h-4 mr-2" }), isDownloading ? 'Downloading...' : `Download ${selectedFormat.toUpperCase()}`] })] })), onMoveToSetup && (_jsxs(Button, { onClick: onMoveToSetup, style: { backgroundColor: '#D97706', color: 'white' }, children: [_jsx(Save, { className: "w-4 h-4 mr-2" }), "Move to Setup"] })), _jsx("button", { onClick: onClose, className: "p-2 rounded-lg transition-colors", style: {
                                    backgroundColor: 'var(--bg-base)',
                                    color: 'var(--text-secondary)'
                                }, onMouseEnter: (e) => {
                                    e.currentTarget.style.backgroundColor = '#ef444420';
                                    e.currentTarget.style.color = '#ef4444';
                                }, onMouseLeave: (e) => {
                                    e.currentTarget.style.backgroundColor = 'var(--bg-base)';
                                    e.currentTarget.style.color = 'var(--text-secondary)';
                                }, children: _jsx(X, { className: "w-5 h-5" }) })] })] }), _jsx("div", { className: "flex-1 overflow-auto print:p-0", style: { padding: viewMode === 'letter' ? '0' : '2rem' }, children: viewMode === 'standard' ? (_jsx("div", { className: "max-w-5xl mx-auto bg-white rounded-xl shadow-lg print:shadow-none print:rounded-none", style: {
                        minHeight: '100%'
                    }, children: _jsx("div", { className: "p-12 print:p-8", children: _jsx(MarkdownViewer, { url: markdownUrl }) }) })) : (_jsx(LetterSizePreview, { content: generatedMarkdown, documentTitle: documentTitle, showDownloadButton: true })) }), _jsx("style", { children: `
        @media print {
          body {
            background: white !important;
          }
          .print\\:hidden {
            display: none !important;
          }
          .print\\:p-0 {
            padding: 0 !important;
          }
          .print\\:p-8 {
            padding: 2rem !important;
          }
          .print\\:shadow-none {
            box-shadow: none !important;
          }
          .print\\:rounded-none {
            border-radius: 0 !important;
          }
        }
      ` })] }));
}
